import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import { AlertTriangle, Clock, Lock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Shown at the top of protected pages when the tenant is trialing,
 * and especially when the trial is expired (blocking state).
 * Polls /billing/status every 5 minutes so the countdown stays fresh.
 */
export default function TrialBanner() {
  const { getAuthHeaders, user } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [trial, setTrial] = useState(null);
  const [dismissedOn, setDismissedOn] = useState(() =>
    sessionStorage.getItem('trialBannerDismissed') || ''
  );

  useEffect(() => {
    if (!user?.tenant_id) return;
    let cancelled = false;
    const load = async () => {
      try {
        const r = await fetch(`${API}/billing/status`, {
          headers: getAuthHeaders(),
          credentials: 'include',
        });
        if (r.ok) {
          const data = await r.json();
          if (!cancelled) setTrial(data.trial || null);
        }
      } catch {
        /* ignore */
      }
    };
    load();
    const id = setInterval(load, 5 * 60 * 1000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [user?.tenant_id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!trial?.trialing || trial.days_remaining == null) return null;

  const lbl = (es, en) => (language === 'es' ? es : en);
  const daysLeft = trial.days_remaining;
  const expired = trial.expired;
  const urgent = expired || daysLeft <= 3;

  // Allow dismissing the friendly banner once per session when there are >3 days.
  // The expired/urgent variants are NOT dismissable.
  const dismissable = !urgent;
  const dismissedToday = dismissedOn === new Date().toDateString();
  if (dismissable && dismissedToday) return null;

  const goUpgrade = () => navigate('/dashboard/settings?tab=billing');

  if (expired) {
    return (
      <div className="mb-6 rounded-md border border-destructive bg-destructive/10 p-5 flex items-start gap-3">
        <Lock className="w-6 h-6 text-destructive shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="font-semibold text-foreground">
            {lbl('Su periodo de prueba ha finalizado', 'Your trial has ended')}
          </p>
          <p className="text-sm text-muted-foreground mt-1">
            {lbl(
              'Active su suscripcion para recuperar el acceso completo a ERP Legal.',
              'Activate your subscription to restore full access to ERP Legal.'
            )}
          </p>
        </div>
        <Button onClick={goUpgrade} className="btn-accent shrink-0">
          {lbl('Activar suscripcion', 'Activate subscription')}
        </Button>
      </div>
    );
  }

  const Icon = urgent ? AlertTriangle : Clock;
  const bgClass = urgent ? 'border-bronze bg-bronze/10' : 'border-border bg-secondary/40';
  const iconClass = urgent ? 'text-bronze' : 'text-muted-foreground';

  return (
    <div className={`mb-6 rounded-md border ${bgClass} p-4 flex items-center gap-3`}>
      <Icon className={`w-5 h-5 ${iconClass} shrink-0`} />
      <div className="flex-1">
        <p className="text-sm">
          {lbl(
            `Periodo de prueba: ${daysLeft} ${daysLeft === 1 ? 'dia' : 'dias'} restantes.`,
            `Trial period: ${daysLeft} ${daysLeft === 1 ? 'day' : 'days'} left.`
          )}
          {trial.card_required && (
            <span className="ml-1 text-muted-foreground">
              {lbl(
                'Al finalizar, se cobrara automaticamente el plan seleccionado.',
                'At the end of the trial, your selected plan will be charged automatically.'
              )}
            </span>
          )}
        </p>
      </div>
      <Button onClick={goUpgrade} size="sm" variant="outline" className="shrink-0">
        {lbl('Activar ahora', 'Activate now')}
      </Button>
      {dismissable && (
        <button
          onClick={() => {
            setDismissedOn(new Date().toDateString());
            sessionStorage.setItem('trialBannerDismissed', new Date().toDateString());
          }}
          className="text-muted-foreground hover:text-foreground text-xl leading-none px-1"
          aria-label="Dismiss"
        >
          ×
        </button>
      )}
    </div>
  );
}
